import { useMemo, useState } from "react"

interface Option {
  value: string
  label: string
}

/**
 * Combobox tipo busqueda para campos respaldados por un catalogo cerrado
 * (version de Minecraft, version de loader) -- TODO.md, correccion pedida
 * por Joanfer: "los campos abiertos no tienen mucho sentido, nadie se sabe
 * las versiones de memoria". El usuario escribe (ej. "1.12") y la lista se
 * filtra en vivo a las opciones cuyo valor empieza con ese texto (todas las
 * `1.12.*`), sacadas del catalogo ya cargado -- nunca un `<input>` de texto
 * libre para un valor de un conjunto cerrado y conocido.
 *
 * El filtro es client-side sobre `options` (la lista completa ya vino del
 * catalogo dinamico, ver CreateServerForm.tsx): no dispara un fetch por
 * tecla, solo filtra lo que ya esta en memoria.
 */
export function SearchableVersionInput({
  label,
  value,
  onChange,
  options,
  disabled,
  loading,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: Option[]
  disabled?: boolean
  loading?: boolean
}) {
  const [query, setQuery] = useState(value)
  const [open, setOpen] = useState(false)
  const id = label.toLowerCase().replace(/\s+/g, "-")

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options.slice(0, 30) // sin texto: mostrar las mas recientes primero (options ya viene ordenado asi), no las 300+ de un tiro
    return options.filter((opt) => opt.value.toLowerCase().startsWith(q)).slice(0, 30)
  }, [query, options])

  function select(opt: Option) {
    setQuery(opt.value)
    onChange(opt.value)
    setOpen(false)
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", position: "relative" }}>
      <label htmlFor={id} style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-medium)" }}>
        {label}
      </label>
      <input
        id={id}
        value={loading ? "Cargando..." : query}
        disabled={disabled || loading}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay so a click on a dropdown option registers before the list unmounts.
          setTimeout(() => setOpen(false), 150)
        }}
        placeholder={`Buscar ${label.toLowerCase()}...`}
        autoComplete="off"
        style={{
          padding: "10px 12px",
          fontSize: "var(--text-base)",
          borderRadius: "var(--radius-sm)",
          border: "1px solid var(--border-default)",
          background: "var(--bg-surface)",
          color: "var(--text-primary)",
        }}
      />
      {open && !loading && filtered.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 4,
            maxHeight: 220,
            overflowY: "auto",
            listStyle: "none",
            padding: "var(--space-1)",
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--border-default)",
            background: "var(--bg-surface-raised)",
            boxShadow: "var(--shadow-lg)",
            zIndex: 10,
          }}
        >
          {filtered.map((opt) => (
            <li key={opt.value}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()} // keep the input's onBlur from firing before onClick
                onClick={() => select(opt)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 10px",
                  background: opt.value === value ? "var(--bg-surface)" : "transparent",
                  border: "none",
                  borderRadius: "var(--radius-sm)",
                  color: "var(--text-primary)",
                  fontSize: "var(--text-sm)",
                  cursor: "pointer",
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !loading && filtered.length === 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: 4,
            padding: "8px 10px",
            fontSize: "var(--text-sm)",
            color: "var(--text-muted)",
          }}
        >
          Sin coincidencias.
        </div>
      )}
    </div>
  )
}
